import { FormControl, styled } from '@mui/material';
import React, { useEffect, useRef } from 'react';

import { resolveKeyedCharacter } from '@sophocles/views/auth/forms/fields/multiFactorCodeInput';

const MultiFactorCodeFieldBase = styled('div')(({ theme }) => {
  return {
    borderRadius: theme.shape.borderRadius,
    '& .mfa-code-input': {
      color: theme.palette.text.primary,
      boxShadow: theme.shadows[1],
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
    },
    '& .mfa-code-input:hover': {
      boxShadow: theme.shadows[3],
    },
    'input.error': {
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: theme.palette.error.main,
    },
  };
});

function MultiFactorCodeField(props) {
  const { characters, allowedCharacters, password, onChange, value, error, ...rest } = props;
  const inputsRef = useRef([]);

  useEffect(() => {
    if (value) {
      let initial = value;
      if (!initial) {
        return;
      }
      if (typeof initial === 'object') {
        throw new Error(
          `Invalid type provided to component. Type must be primitive or function. Supplied: ${typeof iniital}`,
        );
      } else if (typeof initial === 'function') {
        initial = initial();
      }
      initial = initial.toString();
      if (initial.length != inputsRef.length) {
        throw new Error(`Invalid length supplied. Length must be: ${characters}. Supplied: ${initial.length}`);
      }
      initial.forEach((c, i) => {
        inputsRef.current[i] = c;
      });
    }
  }, []);

  const sendResult = () => {
    const res = inputsRef.current.map(input => input.value).join('');
    onChange(res);
  };

  const handleOnKeyDown = e => {
    const { key } = e;
    const { target } = e;
    if (key === 'Backspace') {
      if (target.value === '' && target.previousElementSibling !== null) {
        if (target.previousElementSibling !== null) {
          target.previousElementSibling.focus();
          e.preventDefault();
        }
      } else {
        target.value = '';
      }
      sendResult();
    } else {
      const { advance, value: nextValue } = resolveKeyedCharacter(key, target.value, allowedCharacters);
      if (target.value !== nextValue) {
        target.value = nextValue;
      }
      if (advance) {
        if (target.nextElementSibling !== null) {
          target.nextElementSibling.focus();
        } else {
          target.blur();
        }
      }
      sendResult();
    }
  };

  const handleOnFocus = e => {
    e.target.select();
  };

  const handleOnPaste = e => {
    const value = e.clipboardData.getData('Text');
    if (value.match(allowedCharacters)) {
      for (let i = 0; i < characters && i < value.length; i++) {
        inputsRef.current[i].value = value.charAt(i);
        if (inputsRef.current[i].nextElementSibling !== null) {
          inputsRef.current[i].nextElementSibling.focus();
        }
      }
      sendResult();
    }
    e.preventDefault();
  };

  const inputs = [];
  for (let i = 0; i < characters; i++) {
    inputs.push(
      <input
        key={i}
        onKeyUp={handleOnKeyDown}
        onFocus={handleOnFocus}
        onPaste={handleOnPaste}
        type={password ? 'password' : 'text'}
        ref={el => (inputsRef.current[i] = el)}
        maxLength={1}
        autoComplete="off"
        inputMode="number"
        className={`mfa-code-input ${error ? 'error' : ''}`}
      />,
    );
  }

  return (
    <FormControl {...rest}>
      <MultiFactorCodeFieldBase className="mfa-code">{inputs}</MultiFactorCodeFieldBase>
    </FormControl>
  );
}

MultiFactorCodeField.defaultProps = {
  characters: 6,
  allowedCharacters: '^[0-9]*$',
};

export default MultiFactorCodeField;
